/**
 * 画面が使う操作と、API契約（contracts/openapi.json）のメソッド・パス。
 *
 * HTTP モードでは、契約から作った一覧（src/api/generated/operations.json。`npm run gen:api`）に
 * 同じメソッド・パスがあるものだけを「接続済み」とし、それ以外は呼ばずに「未接続」と表示する。
 * モックでも同じ一覧を使い、契約に無い機能は画面に出さない（モックだけで動いて見えることがないように）。
 *
 * 2026-10-13 15:40（UTC）に配置された契約 0.1.0 に合わせた。契約に無いものは「★」として docs/API_NEEDS.md で依頼している。
 */

export const OPERATIONS = {
  // ログイン状態・権限
  me: ["GET", "/api/v1/me"],
  logout: ["POST", "/api/v1/logout"],
  // 再生
  playback: ["GET", "/api/v1/playback"],
  queue: ["GET", "/api/v1/queue"],
  "commands.create": ["POST", "/api/v1/playback/commands"],
  "commands.get": ["GET", "/api/v1/commands/{id}"],
  events: ["GET", "/api/v1/events"],
  // ここ好き（公開・自分用は PUT の visibility で切りかえる）
  "moments.capture": ["POST", "/api/v1/moments/capture"],
  "moments.list": ["GET", "/api/v1/moments"],
  "moments.get": ["GET", "/api/v1/moments/{id}"],
  "moments.edit": ["PUT", "/api/v1/moments/{id}"],
  "moments.delete": ["DELETE", "/api/v1/moments/{id}"],
  // 見つける・検索
  "performances.list": ["GET", "/api/v1/performances"],
  "posts.list": ["GET", "/api/v1/posts"],
  "catalog.search": ["GET", "/api/v1/catalog/search"],
  "catalog.inspect": ["GET", "/api/v1/catalog/inspect"],
  // 曲・歌唱の登録と修正
  "works.search": ["GET", "/api/v1/works"],
  "works.create": ["POST", "/api/v1/works"],
  "works.get": ["GET", "/api/v1/works/{id}"],
  "performers.search": ["GET", "/api/v1/performers"],
  "performers.create": ["POST", "/api/v1/performers"],
  "performances.create": ["POST", "/api/v1/performances"],
  "performances.get": ["GET", "/api/v1/performances/{id}"],
  "performances.edit": ["PUT", "/api/v1/performances/{id}"],
  "segments.list": ["GET", "/api/v1/performances/{id}/segments"],
  "segments.create": ["POST", "/api/v1/performances/{id}/segments"],
  // 投稿
  "posts.get": ["GET", "/api/v1/posts/{id}"],
  "posts.create": ["POST", "/api/v1/posts"],
  "posts.edit": ["PUT", "/api/v1/posts/{id}"],
  "posts.delete": ["DELETE", "/api/v1/posts/{id}"],
  // 保存・ライブラリ・プレイリスト
  library: ["GET", "/api/v1/library"],
  "library.save": ["POST", "/api/v1/library"],
  "library.unsave": ["DELETE", "/api/v1/library/{id}"],
  "playlists.list": ["GET", "/api/v1/playlists"],
  "playlists.get": ["GET", "/api/v1/playlists/{id}"],
  "playlists.create": ["POST", "/api/v1/playlists"],
  "playlists.edit": ["PUT", "/api/v1/playlists/{id}"],
  "playlists.delete": ["DELETE", "/api/v1/playlists/{id}"],
  // 履歴
  history: ["GET", "/api/v1/history"],
  // 通報・運営（非表示は通報に対して行う）
  "reports.create": ["POST", "/api/v1/reports"],
  "reports.list": ["GET", "/api/v1/reports"],
  "reports.moderate": ["PUT", "/api/v1/reports/{id}"],
  // 契約に無い操作をここに足すときは「★」を付け、docs/API_NEEDS.md で Codex に依頼する（画面は「未接続」と出す）
} as const satisfies Record<string, readonly [string, string]>;

export type OperationId = keyof typeof OPERATIONS;

/**
 * ログインはページ遷移（/auth/login）で、OpenAPI には載っていない（contracts/README.md に記載）。
 * /api/v1/me が契約にあれば、ログインの入口もあるとみなす
 */
export const LOGIN_PATH = "/auth/login";

export function operationKey(op: OperationId): string {
  const [method, path] = OPERATIONS[op];
  return `${method} ${path}`;
}

/** パスの {name} を埋める */
export function fillPath(path: string, params: Record<string, string> = {}): string {
  return path.replace(/\{(\w+)\}/g, (_, name: string) => {
    const v = params[name];
    if (v === undefined) throw new Error(`path parameter ${name} is missing`);
    return encodeURIComponent(v);
  });
}

/* ---- 契約にある操作の一覧（`npm run gen:api` で作る）。無ければ空＝すべて未接続 ---- */

const generated = import.meta.glob<string[]>("./generated/operations.json", { eager: true, import: "default" });
const CONTRACT_OPERATIONS = new Set<string>(Object.values(generated)[0] ?? []);

export function inContract(op: OperationId): boolean {
  return CONTRACT_OPERATIONS.has(operationKey(op));
}

export function contractLoaded(): boolean {
  return CONTRACT_OPERATIONS.size > 0;
}
